import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import i18n, { applyLocaleToDocument } from "../../i18n";
import { supabase } from "../../lib/supabase";
import { getPasswordResetRedirectUrl } from "../../lib/pwa";
import { AuthContext, type AuthState, type EmployeeAccessContext } from "./auth-context";
import { mapPasswordResetError, mapPasswordUpdateError } from "./password-policy";
import { clearActivity, isSessionExpired, readLastActivity, recordActivity, sessionStartedAt } from "./session-policy";

const accessSchema = z.object({
  employee_id: z.uuid(),
  work_email: z.email(),
  full_name_ar: z.string().nullable(),
  full_name_en: z.string(),
  status: z.enum(["invited", "active", "disabled"]),
  locale: z.enum(["ar", "en"]),
  display_timezone: z.literal("Asia/Riyadh"),
  primary_team_id: z.uuid().nullable(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
});

const mapAccess = (value: z.infer<typeof accessSchema>): EmployeeAccessContext => ({
  employeeId: value.employee_id,
  workEmail: value.work_email,
  fullNameAr: value.full_name_ar,
  fullNameEn: value.full_name_en,
  status: value.status,
  locale: value.locale,
  displayTimezone: value.display_timezone,
  primaryTeamId: value.primary_team_id,
  roles: value.roles,
  permissions: value.permissions,
});

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>({ kind: "loading" });
  const recoveryFlowActive = useRef(window.location.pathname === "/reset-password");

  const enforceSessionPolicy = useCallback(async (recordCurrentActivity = false) => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session) return false;
    const storage = window.localStorage;
    if (isSessionExpired(sessionStartedAt(session), readLastActivity(session, storage))) {
      clearActivity(session, storage);
      await supabase.auth.signOut({ scope: "global" });
      setState({ kind: "signed_out" });
      return false;
    }
    if (recordCurrentActivity) recordActivity(session, storage);
    return true;
  }, []);

  const loadAccess = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      setState({ kind: window.location.pathname === "/reset-password" ? "recovery_invalid" : "signed_out" });
      return;
    }
    if (!await enforceSessionPolicy()) return;
    if (recoveryFlowActive.current || window.location.pathname === "/reset-password") {
      setState({ kind: "recovery" });
      return;
    }

    const { data, error } = await supabase.rpc("get_my_access_context");
    if (error) {
      setState({ kind: "error", message: "access_context_failed" });
      return;
    }

    const parsed = accessSchema.safeParse(data);
    if (!parsed.success) {
      setState({ kind: "error", message: "invalid_access_context" });
      return;
    }

    const access = mapAccess(parsed.data);
    await i18n.changeLanguage(access.locale);
    applyLocaleToDocument(access.locale);
    setState({ kind: access.status, access });
  }, [enforceSessionPolicy]);

  useEffect(() => {
    void loadAccess();
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        recoveryFlowActive.current = true;
        window.history.replaceState({}, "", "/reset-password");
        setState({ kind: "recovery" });
      } else if (event === "SIGNED_OUT") {
        setState({ kind: "signed_out" });
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        window.setTimeout(() => void loadAccess(), 0);
      }
    });
    const recordActiveUse = () => { if (document.visibilityState === "visible") void enforceSessionPolicy(true); };
    const checkInterval = window.setInterval(() => void enforceSessionPolicy(), 60_000);
    window.addEventListener("pointerdown", recordActiveUse);
    window.addEventListener("keydown", recordActiveUse);
    document.addEventListener("visibilitychange", recordActiveUse);
    return () => {
      data.subscription.unsubscribe();
      window.clearInterval(checkInterval);
      window.removeEventListener("pointerdown", recordActiveUse);
      window.removeEventListener("keydown", recordActiveUse);
      document.removeEventListener("visibilitychange", recordActiveUse);
    };
  }, [enforceSessionPolicy, loadAccess]);

  const value = useMemo(() => ({
    state,
    signIn: async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
      if (!error) return null;
      if (error.code === "invalid_credentials") return "invalid_credentials";
      if (error.code === "email_not_confirmed") return "email_not_confirmed";
      if (error.code === "over_request_rate_limit") return "rate_limited";
      return "request_failed";
    },
    requestPasswordReset: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: getPasswordResetRedirectUrl(window.location.origin),
      });
      return mapPasswordResetError(error);
    },
    verifyRecoveryCode: async (email: string, token: string) => {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: token.replace(/\s/g, ""),
        type: "recovery",
      });
      if (error) return "recovery_code_invalid";
      recoveryFlowActive.current = true;
      window.history.replaceState({}, "", "/reset-password");
      setState({ kind: "recovery" });
      return null;
    },
    updatePassword: async (password: string) => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return "recovery_session_missing";
      const { error } = await supabase.auth.updateUser({ password });
      if (!error) {
        recoveryFlowActive.current = false;
        window.history.replaceState({}, "", "/");
        await loadAccess();
      }
      return mapPasswordUpdateError(error);
    },
    signOut: async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) clearActivity(data.session, window.localStorage);
      await supabase.auth.signOut();
    },
  }), [loadAccess, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
